import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Coins } from 'lucide-react';
import { formatNaira } from '../../utils/money';

const describe = (min, max) => {
  if (min && max) return `${formatNaira(min)} – ${formatNaira(max)}`;
  if (min) return `From ${formatNaira(min)}`;
  return `Up to ${formatNaira(max)}`;
};

// Drafts are committed on Apply so typing "5000" does not fire four requests.
const AmountRangeFilter = ({ min = '', max = '', onChange, label = 'Amount', className = '' }) => {
  const [open, setOpen] = useState(false);
  const [draftMin, setDraftMin] = useState(min);
  const [draftMax, setDraftMax] = useState(max);
  const ref = useRef(null);
  const active = Boolean(min || max);
  const invalid = draftMin !== '' && draftMax !== '' && Number(draftMax) < Number(draftMin);

  useEffect(() => {
    if (!open) return undefined;
    setDraftMin(min);
    setDraftMax(max);
    const onPointer = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => event.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, min, max]);

  const apply = (event) => {
    event.preventDefault();
    if (invalid) return;
    onChange({ min: draftMin, max: draftMax });
    setOpen(false);
  };

  const clear = () => {
    onChange({ min: '', max: '' });
    setOpen(false);
  };

  return (
    <div ref={ref} className={`relative inline-block ${className}`}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className={[
          'inline-flex items-center gap-1.5 rounded border px-2 py-1 text-sm transition-colors',
          active
            ? 'border-primary-500 text-primary-600 bg-primary-500/5'
            : 'border-theme-border text-theme-bodycolor hover:border-primary-300'
        ].join(' ')}
      >
        <Coins size={13} />
        <span className="whitespace-nowrap">{active ? describe(min, max) : label}</span>
        <ChevronDown size={12} />
      </button>

      <form
        hidden={!open}
        onSubmit={apply}
        className="absolute left-0 top-full z-20 mt-1 w-56 rounded bg-theme-cardbg p-3 shadow-[0_4px_24px_0_rgba(62,57,107,.18)]"
      >
        <label className="mb-2 block">
          <span className="mb-1 block text-sm text-theme-secondarytextcolor">Minimum (₦)</span>
          <input
            type="number"
            min="0"
            step="any"
            className="form-control form-control-sm"
            value={draftMin}
            onChange={(event) => setDraftMin(event.target.value)}
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-sm text-theme-secondarytextcolor">Maximum (₦)</span>
          <input
            type="number"
            min="0"
            step="any"
            className="form-control form-control-sm"
            value={draftMax}
            onChange={(event) => setDraftMax(event.target.value)}
          />
        </label>
        {invalid && <p className="mt-1 text-sm text-danger-600">Maximum must be at least the minimum.</p>}
        <div className="mt-2 flex items-center justify-between">
          <button type="submit" className="btn btn-primary btn-sm" disabled={invalid}>
            Apply
          </button>
          {active && (
            <button type="button" className="text-sm text-primary-600 hover:underline" onClick={clear}>
              Clear
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default AmountRangeFilter;
